<script setup>
import { computed } from 'vue'
import { useODataSource } from '@/composables/data-grid'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useFocusRestore } from '@/composables/focus-restore'

import { DxTextBox } from 'devextreme-vue/text-box'
import DxValidator from 'devextreme-vue/validator'

import GridDropDownBox from '@/components/shared/GridDropDownBox.vue'

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  value: {
    type: Number,
    default: null,
  },
  valueText: {
    type: String,
    default: null,
  },
  country: {
    type: Number,
    default: null,
  },
  state: {
    type: Number,
    default: null,
  },
  zipCode: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  filteredCitiesCount: {
    type: Number,
    default: null,
  },
  validationGroup: {
    type: String,
    default: undefined,
  },
  validationRules: {
    type: Array,
    default: () => [],
  },
})

const webSiteStore = useWebSiteStore()

const defaultDataSource = useODataSource(webSiteStore.api.getAbsoluteUrl() + webSiteStore.api.citiesUrl, {
  searchExpr: ['name', 'code'],
})

const computedDataSource = computed(() => {
  const ds = defaultDataSource
  if (props.country) {
    ds.filter(['country', '=', props.country])
  }
  if (props.state) {
    ds.filter(['state', '=', props.state])
  }
  if (props.zipCode) {
    ds.filter(['zip_code', 'contains', props.zipCode])
  }
  return ds
})

const hasStructuredLocationData = computed(() => !!webSiteStore.countries.find(c => c.id === props.country)?.has_structured_location_data)

const emit = defineEmits({
  valueChanged: (e) => true,
})

const validFilteredCitiesCount = computed(() => {
  return props.filteredCitiesCount == null || props.filteredCitiesCount > 0
})

const isValueTextNullOrEmpty = computed(() => {
  return !props.valueText || props.valueText.trim() === ''
})

const showDropDown = computed(() => hasStructuredLocationData.value && validFilteredCitiesCount.value && isValueTextNullOrEmpty.value)

const { rootRef, onFocusIn, onFocusOut } = useFocusRestore('city', { trackInnerFocus: true })
</script>

<template>
  <div
    ref="rootRef"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <grid-drop-down-box
      v-if="showDropDown"
      :value="props.value"
      :data-source="computedDataSource"
      :disabled="props.disabled"
      :validation-group="props.validationGroup"
      :validation-rules="props.validationRules"
      :columns="[
        { dataField: 'code', caption: $t('common.code') },
        { dataField: 'name', caption: $t('common.name'), sortOrder: 'asc' },
        { dataField: 'zip_code', caption: $t('common.zipCode') },
      ]"
      @value-changed="(e) => {
        emit('valueChanged', { value: e })
        props.data?.setValue(e)
      }"
    />
    <dx-text-box
      v-else
      :value="props.valueText"
      :disabled="props.disabled"
      @value-changed="(e) => {
        emit('valueChanged', { value: e.value })
        props.data?.setValue(e.value)
      }"
    >
      <dx-validator
        v-if="props.validationRules.length"
        :validation-group="props.validationGroup"
        :validation-rules="props.validationRules"
      />
    </dx-text-box>
  </div>
</template>
